import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { PHONE_REGEX, normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';
import { LICENSE_NO_REGEX, toLicenseNoInputValue } from '../utils/license-no';

import { useMedicalStaffOptionsQuery } from '../hooks/use-medical-staff';
import {
  useApproveWithdrawMedicalStaff,
  useRejectWithdrawMedicalStaff,
  useResendMedicalStaffInvitation,
  useUpdateMedicalStaff,
  useWithdrawMedicalStaff,
} from '../hooks/use-medical-staff-mutations';
import { useTranslateOption } from '../hooks/use-translate-option';
import { useMedicalStaffEditStore } from '../store/medical-staff-edit.store';
import { MedicalStaffApiError } from '../types/medical-staff.types';
import { formatRegistrationDate } from '../utils/format-date';
import { MedicalStaffResendPreviewModal } from './MedicalStaffResendPreviewModal';
import { MedicalStaffStatusBadge } from './MedicalStaffStatusBadge';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

// Send-time chrome string for the resend preview card meta (YYYY-MM-DD HH:mm),
// captured once when the preview opens so it doesn't tick while the modal is up.
function formatSentAt(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

interface EditFormState {
  name: string;
  // SHCT-695 — held as bare digits, matching what the input now accepts. It used
  // to be seeded display-formatted ("010-1234-5678"), which is the rendering rule
  // ([공통] 00 §2.1: "화면 표시 시에만" hyphens), not the input rule. Validated
  // against PHONE_REGEX on submit.
  phone: string;
  department: string;
  jobRole: string;
  // SHCT-991 — optional; '' submits as a clear.
  licenseNo: string;
}

type FieldKey = 'name' | 'phone' | 'department' | 'jobRole' | 'licenseNo';

const EMPTY_FORM: EditFormState = {
  name: '',
  phone: '',
  department: '',
  jobRole: '',
  licenseNo: '',
};

// PRD 03 §3.6 / §3.7 (v1.2): email immutable; status is read-only (system-
// managed). Action cluster lives in the panel footer — Withdraw and Resend
// migrated off the row per spec. Password reset arrives in Slice 3.
export function MedicalStaffEditPanel() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  // STAFF:READ can open the panel for detail view; STAFF:WRITE is required
  // to save edits or run any destructive/state-changing action.
  const { canWrite, denyProps, tooltip } = useWriteGuard('STAFF');
  const currentEmail = useAuthStore((s) => s.email);
  const { translateDepartment, translateJobRole } = useTranslateOption();
  const { data: options } = useMedicalStaffOptionsQuery();
  const updateMutation = useUpdateMedicalStaff();
  const resendMutation = useResendMedicalStaffInvitation();
  const withdrawMutation = useWithdrawMedicalStaff();
  const approveWithdrawMutation = useApproveWithdrawMedicalStaff();
  const rejectWithdrawMutation = useRejectWithdrawMedicalStaff();

  const editing = useMedicalStaffEditStore((s) => s.editing);
  const closeStore = useMedicalStaffEditStore((s) => s.close);

  const [form, setForm] = useState<EditFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showApproveWithdrawConfirm, setShowApproveWithdrawConfirm] = useState(false);
  const [showRejectWithdrawConfirm, setShowRejectWithdrawConfirm] = useState(false);
  const [resendPreviewOpen, setResendPreviewOpen] = useState(false);
  const [resendSentAt, setResendSentAt] = useState('');

  const departments = options?.departments ?? [];
  const jobRoles = options?.jobRoles ?? [];

  // PRD 03 §3.6 self-guard: admin and staff account spaces are nominally
  // separate, so email-match is best-effort (matches the row's prior logic).
  const isSelf = Boolean(currentEmail) && currentEmail === editing?.email;

  // Sync form with the row being opened. Fires only when editing.id changes,
  // so user edits inside the panel are not clobbered mid-session.
  useEffect(() => {
    if (editing) {
      // Bare digits: the edit field is an input, not a display surface.
      const digitsPhone = normalizePhone(editing.phone) ?? '';
      setForm({
        name: editing.name,
        phone: digitsPhone,
        department: editing.department,
        jobRole: editing.jobRole,
        licenseNo: editing.licenseNo ?? '',
      });
      setErrors({});
      setIsDirty(false);
      setShowDiscard(false);
      setShowWithdrawConfirm(false);
      setShowApproveWithdrawConfirm(false);
      setShowRejectWithdrawConfirm(false);
    }
  }, [editing?.id]);

  const update = <K extends FieldKey>(key: K, value: EditFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Per-field validation — shared by the on-blur handler (immediate feedback when
  // the user leaves a field) and the submit-time full sweep.
  const validateField = (key: FieldKey, f: EditFormState): string | undefined => {
    switch (key) {
      case 'name': {
        const name = f.name.trim();
        if (name.length < 2 || name.length > 50) {
          return t('medicalStaffManagement.fields.name.errorRequired');
        }
        return undefined;
      }
      case 'phone': {
        const normalizedPhone = normalizePhone(f.phone);
        if (!normalizedPhone) return t('medicalStaffManagement.fields.phone.errorRequired');
        if (!PHONE_REGEX.test(normalizedPhone)) {
          return t('medicalStaffManagement.fields.phone.errorInvalid');
        }
        return undefined;
      }
      case 'department':
        return f.department
          ? undefined
          : t('medicalStaffManagement.fields.department.errorRequired');
      case 'jobRole':
        return f.jobRole
          ? undefined
          : t('medicalStaffManagement.fields.jobRole.errorRequired');
      // SHCT-991 — optional; only a non-empty value is checked.
      case 'licenseNo':
        return !f.licenseNo || LICENSE_NO_REGEX.test(f.licenseNo)
          ? undefined
          : t('medicalStaffManagement.fields.licenseNo.errorInvalid');
    }
  };

  // Validate a single field when the user leaves it (blur). Clears the error when
  // the field becomes valid so the message doesn't linger after a fix.
  const handleBlur = (key: FieldKey) => {
    setErrors((prev) => {
      const err = validateField(key, form);
      const next = { ...prev };
      if (err) next[key] = err;
      else delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    (['name', 'phone', 'department', 'jobRole', 'licenseNo'] as FieldKey[]).forEach((key) => {
      const err = validateField(key, form);
      if (err) next[key] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const busy =
    updateMutation.isPending ||
    resendMutation.isPending ||
    withdrawMutation.isPending ||
    approveWithdrawMutation.isPending ||
    rejectWithdrawMutation.isPending;

  // SHCT-582 — PRD 21 §7.4 P-2. QC's re-test opened this panel with STAFF:READ and found 이름 ·
  // 연락처 · 진료과 · 직무 all still editable: only the footer was gated, so the operator could
  // retype a staff record and learn at [저장] that none of it could be kept. The 이메일 field was
  // already `readOnly` for its own reason (§3.4 — it is the login id) and is left alone.
  const fieldsDisabled = !canWrite || busy;

  const handleClose = () => {
    if (busy) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closeStore();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    closeStore();
  };

  const handleSubmit = async () => {
    if (!editing || updateMutation.isPending) return;
    if (!validate()) return;
    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        input: {
          name: form.name.trim(),
          phone: normalizePhone(form.phone),
          department: form.department,
          jobRole: form.jobRole,
          licenseNo: form.licenseNo,
        },
      });
      closeStore();
      toast.success(t('medicalStaffManagement.toasts.updateSuccess'));
    } catch (e) {
      if (e instanceof MedicalStaffApiError) {
        if (e.code === 'PHONE_DUPLICATE') {
          // Anti-enumeration: a phone already used by another staff shows the single shared generic
          // message (locale-aware), inline on the phone field. The DB unique-phone rule returns
          // PHONE_DUPLICATE without a field map, so map the code here.
          setErrors((prev) => ({ ...prev, phone: t('errors.phoneRejected', { ns: 'common' }) }));
          return;
        }
        if (e.code === 'LICENSE_NO_DUPLICATE') {
          setErrors((prev) => ({
            ...prev,
            licenseNo: t('medicalStaffManagement.fields.licenseNo.errorDuplicate'),
          }));
          return;
        }
        if (e.fields) {
          setErrors(e.fields as Partial<Record<FieldKey, string>>);
          return;
        }
        // System/unknown API failures surface the action-specific message, not the
        // raw mapped `e.message` (which is the generic UNKNOWN fallback). Field &
        // business errors are handled above (fields / PHONE_DUPLICATE).
        toast.error(t('medicalStaffManagement.toasts.updateError'));
        return;
      }
      toast.error(t('medicalStaffManagement.toasts.updateError'));
    }
  };

  // [재발송] — show the pre-send confirmation modal first (resend variant),
  // populated with the staff's stored values.
  const handleResendPreview = () => {
    if (!editing || resendMutation.isPending) return;
    setResendSentAt(formatSentAt(new Date()));
    setResendPreviewOpen(true);
  };

  const handleResend = async () => {
    if (!editing || resendMutation.isPending) return;
    try {
      await resendMutation.mutateAsync(editing.id);
      setResendPreviewOpen(false);
      toast.success(
        t('medicalStaffManagement.toasts.resendSuccess', { email: editing.email }),
      );
    } catch (e) {
      setResendPreviewOpen(false);
      const message =
        e instanceof MedicalStaffApiError
          ? e.message
          : t('medicalStaffManagement.toasts.resendError');
      toast.error(message);
    }
  };

  const handleWithdrawConfirm = async () => {
    if (!editing) return;
    try {
      await withdrawMutation.mutateAsync(editing.id);
      setShowWithdrawConfirm(false);
      closeStore();
      toast.success(
        t('medicalStaffManagement.toasts.withdrawSuccess', { name: editing.name }),
      );
    } catch (e) {
      const message =
        e instanceof MedicalStaffApiError
          ? e.message
          : t('medicalStaffManagement.toasts.withdrawError');
      toast.error(message);
      setShowWithdrawConfirm(false);
    }
  };

  const handleApproveWithdrawConfirm = async () => {
    if (!editing) return;
    try {
      await approveWithdrawMutation.mutateAsync(editing.id);
      setShowApproveWithdrawConfirm(false);
      closeStore();
      toast.success(t('medicalStaffManagement.toasts.approveWithdrawSuccess'));
    } catch (e) {
      const message =
        e instanceof MedicalStaffApiError
          ? e.message
          : t('medicalStaffManagement.toasts.approveWithdrawError');
      toast.error(message);
      setShowApproveWithdrawConfirm(false);
    }
  };

  const handleRejectWithdrawConfirm = async () => {
    if (!editing) return;
    try {
      await rejectWithdrawMutation.mutateAsync(editing.id);
      setShowRejectWithdrawConfirm(false);
      closeStore();
      toast.success(t('medicalStaffManagement.toasts.rejectWithdrawSuccess'));
    } catch (e) {
      const message =
        e instanceof MedicalStaffApiError
          ? e.message
          : t('medicalStaffManagement.toasts.rejectWithdrawError');
      toast.error(message);
      setShowRejectWithdrawConfirm(false);
    }
  };

  const writeTooltip = !canWrite
    ? t('medicalStaffManagement.tooltips.noWritePermission')
    : undefined;

  // PRD 03 §3.8 (v1.3): a pending self-withdraw request takes over the panel —
  // the request banner + Approve/Reject replace the plain admin-direct Withdraw.
  const hasWithdrawRequest = Boolean(editing?.withdrawRequest);
  const showResend = editing?.status === 'PENDING';
  const showWithdraw =
    editing != null &&
    editing.status !== 'WITHDRAWN' &&
    !isSelf &&
    !hasWithdrawRequest;
  const showWithdrawRequestActions = hasWithdrawRequest && !isSelf;
  // Only ACTIVE staff can have their password reset (no password yet while
  // PENDING; permanent login block once WITHDRAWN).
  const showResetPassword = editing?.status === 'ACTIVE';
  // SHCT-597 (CR-01 §9) — PRD 03 §3.6 계정 상태별 [수정] 가용성 매트릭스 (Req Clarification Q32, canonical):
  // 미가입은 [수정] 불가이고 [재발송]만 가능하다 ("초대 단계라 정보 수정 대신 재발송으로 처리"). 가입완료·
  // 탈퇴완료는 그대로 수정 가능하다(탈퇴완료도 "패널 진입 가능", 상태만 읽기 전용 라벨). 패널 자체는 계속
  // 열린다 — §3.5 조건분기가 미가입일 때 "이메일 필드는 읽기 전용으로 고정"이라고 적어 진입을 전제하므로,
  // 막는 것은 저장 액션 하나다. 프로토타입은 미가입에도 [저장]을 그리지만 §3.6이 canonical이라 우선한다.
  const canEditFields = editing != null && editing.status !== 'PENDING';
  // The button is disabled rather than removed so the reason is readable on hover — the panel's other
  // gated actions already use this disabled + title pattern for the same reason.
  const pendingNoEditTooltip = !canEditFields
    ? t('medicalStaffManagement.tooltips.pendingNoEdit')
    : undefined;

  const handleResetPassword = () => {
    if (!editing) return;
    // `send=true` enables the preview page's action bar (Cancel/Send). Absent
    // from the header 🔑 shortcut, which renders preview-only mode.
    const q = new URLSearchParams({
      send: 'true',
      id: String(editing.id),
      name: editing.name,
      email: editing.email,
    }).toString();
    navigate(`/staff-password-reset-email?${q}`);
  };

  return (
    <>
      <SlidePanel
        open={editing != null}
        title={t('medicalStaffManagement.editPanel.title')}
        width={480}
        onClose={handleClose}
      >
        {hasWithdrawRequest && editing?.withdrawRequest && (
          // PRD 03 §3.8 (v1.3): surface the pending self-withdraw request so the
          // admin can process it from the detail panel.
          <div
            style={{
              display: 'flex',
              gap: 8,
              background: '#fef2f2',
              border: '1px solid var(--destructive)',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 'var(--space-4)',
              color: '#b91c1c',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <AlertTriangle
              size={16}
              style={{ flexShrink: 0, marginTop: 2 }}
              aria-hidden
            />
            <div>
              <Trans
                t={t}
                i18nKey="medicalStaffManagement.withdrawRequest.bannerTitle"
                values={{
                  date: formatRegistrationDate(editing.withdrawRequest.requestedAt),
                }}
                components={{ strong: <strong /> }}
              />
              <br />
              <Trans
                t={t}
                i18nKey="medicalStaffManagement.withdrawRequest.bannerBody"
                components={{ strong: <strong /> }}
              />
            </div>
          </div>
        )}

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.name.placeholder')}
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            disabled={fieldsDisabled}
            {...denyProps}
          />
          {errors.name && <p className="form-field__error">{errors.name}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.email.label')}
          </label>
          <input
            type="email"
            className="form-field__input"
            value={editing?.email ?? ''}
            readOnly
            style={{ background: 'var(--muted)', cursor: 'not-allowed' }}
          />
          <p
            className="form-field__help"
            style={{
              marginTop: 'var(--space-2)',
              color: 'var(--muted-foreground)',
              fontSize: 12,
            }}
          >
            {t('medicalStaffManagement.editPanel.emailReadOnlyNote')}
          </p>
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.phone.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="tel"
            className={`form-field__input${errors.phone ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.phone.placeholder')}
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => update('phone', toPhoneInputValue(e.target.value))}
            onBlur={() => handleBlur('phone')}
            disabled={fieldsDisabled}
            {...denyProps}
          />
          {errors.phone && <p className="form-field__error">{errors.phone}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.licenseNo.label')}
          </label>
          <input
            type="text"
            className={`form-field__input${errors.licenseNo ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.licenseNo.placeholder')}
            inputMode="numeric"
            value={form.licenseNo}
            onChange={(e) => update('licenseNo', toLicenseNoInputValue(e.target.value))}
            onBlur={() => handleBlur('licenseNo')}
            disabled={fieldsDisabled}
            {...denyProps}
          />
          {errors.licenseNo ? (
            <p className="form-field__error">{errors.licenseNo}</p>
          ) : (
            <p className="form-field__help">{t('medicalStaffManagement.fields.licenseNo.help')}</p>
          )}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.department.label')}{' '}
            <span className="required">*</span>
          </label>
          <select
            className={`form-field__select${errors.department ? ' form-field__select--error' : ''}`}
            value={form.department}
            onChange={(e) => update('department', e.target.value)}
            onBlur={() => handleBlur('department')}
            disabled={fieldsDisabled}
            {...denyProps}
          >
            <option value="">
              {t('medicalStaffManagement.fields.department.placeholder')}
            </option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {translateDepartment(d)}
              </option>
            ))}
          </select>
          {errors.department && (
            <p className="form-field__error">{errors.department}</p>
          )}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.jobRole.label')}{' '}
            <span className="required">*</span>
          </label>
          <select
            className={`form-field__select${errors.jobRole ? ' form-field__select--error' : ''}`}
            value={form.jobRole}
            onChange={(e) => update('jobRole', e.target.value)}
            onBlur={() => handleBlur('jobRole')}
            disabled={fieldsDisabled}
            {...denyProps}
          >
            <option value="">
              {t('medicalStaffManagement.fields.jobRole.placeholder')}
            </option>
            {jobRoles.map((r) => (
              <option key={r} value={r}>
                {translateJobRole(r)}
              </option>
            ))}
          </select>
          {errors.jobRole && <p className="form-field__error">{errors.jobRole}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.status.label')}
          </label>
          {editing && (
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
              <MedicalStaffStatusBadge status={editing.status} />
            </div>
          )}
          <p
            className="form-field__help"
            style={{
              marginTop: 'var(--space-2)',
              color: 'var(--muted-foreground)',
              fontSize: 12,
            }}
          >
            {t('medicalStaffManagement.editPanel.statusHelpText')}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-6)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {showResend && (
              <button
                type="button"
                className="btn btn-default btn-primary"
                disabled={!canWrite || busy}
                {...denyProps}
                title={writeTooltip}
                onClick={handleResendPreview}
              >
                {t('medicalStaffManagement.actions.resend')}
              </button>
            )}
            {showResetPassword && (
              <button
                type="button"
                className="btn btn-default btn-outline"
                style={{
                  color: canWrite ? 'var(--destructive)' : undefined,
                  borderColor: canWrite ? 'var(--destructive)' : undefined,
                }}
                disabled={!canWrite || busy}
                {...denyProps}
                title={writeTooltip}
                onClick={handleResetPassword}
              >
                {t('medicalStaffManagement.actions.resetPassword')}
              </button>
            )}
            {showWithdraw && (
              <button
                type="button"
                className="btn btn-default btn-outline"
                style={{
                  color: canWrite ? 'var(--destructive)' : undefined,
                  borderColor: canWrite ? 'var(--destructive)' : undefined,
                }}
                disabled={!canWrite || busy}
                {...denyProps}
                title={writeTooltip}
                onClick={() => setShowWithdrawConfirm(true)}
              >
                {t('medicalStaffManagement.actions.withdraw')}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleClose}
              disabled={busy}
            >
              {t('medicalStaffManagement.actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handleSubmit}
              disabled={!canWrite || !canEditFields || busy}
              {...denyProps}
              title={!canEditFields ? pendingNoEditTooltip : writeTooltip}
            >
              {updateMutation.isPending
                ? t('medicalStaffManagement.actions.saving')
                : t('medicalStaffManagement.actions.save')}
            </button>
          </div>
        </div>

        {showWithdrawRequestActions && (
          // PRD 03 §3.8 (v1.3) / prototype: withdrawal-request actions live on their
          // own row below a divider, separated from the standard footer cluster.
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-4)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--border)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-default btn-outline"
              style={{
                color: canWrite ? 'var(--destructive)' : undefined,
                borderColor: canWrite ? 'var(--destructive)' : undefined,
              }}
              disabled={!canWrite || busy}
              {...denyProps}
              title={writeTooltip}
              onClick={() => setShowApproveWithdrawConfirm(true)}
            >
              {t('medicalStaffManagement.actions.approveWithdraw')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-outline"
              disabled={!canWrite || busy}
              {...denyProps}
              title={writeTooltip}
              onClick={() => setShowRejectWithdrawConfirm(true)}
            >
              {t('medicalStaffManagement.actions.rejectWithdraw')}
            </button>
            <a
              href={`/staff-withdraw-result-email?name=${encodeURIComponent(editing?.name ?? '')}`}
              target="_blank"
              rel="noopener noreferrer"
              title={t('medicalStaffManagement.tooltips.previewWithdrawResultEmail')}
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                color: 'var(--info, #2563eb)',
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              }}
            >
              {t('medicalStaffManagement.withdrawRequest.previewEmailLink')}
            </a>
          </div>
        )}
      </SlidePanel>

      <ConfirmModal
        open={showDiscard}
        title={t('medicalStaffManagement.invitePanel.discardTitle')}
        message={t('medicalStaffManagement.invitePanel.discardMessage')}
        confirmLabel={t('medicalStaffManagement.modals.discard.confirm')}
        cancelLabel={t('medicalStaffManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />

      <ConfirmModal
        open={showWithdrawConfirm}
        title={t('medicalStaffManagement.confirmModals.withdraw.title')}
        message={t('medicalStaffManagement.confirmModals.withdraw.message', {
          name: editing?.name ?? '',
        })}
        confirmLabel={t('medicalStaffManagement.confirmModals.withdraw.confirm')}
        cancelLabel={t('medicalStaffManagement.confirmModals.withdraw.cancel')}
        confirmVariant="destructive"
        loading={withdrawMutation.isPending}
        loadingLabel={t('medicalStaffManagement.confirmModals.withdraw.confirm')}
        onConfirm={handleWithdrawConfirm}
        onCancel={() => setShowWithdrawConfirm(false)}
      />

      <ConfirmModal
        open={showApproveWithdrawConfirm}
        title={t('medicalStaffManagement.confirmModals.approveWithdraw.title')}
        message={`${t('medicalStaffManagement.confirmModals.approveWithdraw.message', {
          name: editing?.name ?? '',
        })} ${t('medicalStaffManagement.confirmModals.approveWithdraw.emailNote', {
          name: editing?.name ?? '',
        })}`}
        confirmLabel={t('medicalStaffManagement.confirmModals.approveWithdraw.confirm')}
        cancelLabel={t('medicalStaffManagement.confirmModals.approveWithdraw.cancel')}
        confirmVariant="destructive"
        loading={approveWithdrawMutation.isPending}
        loadingLabel={t('medicalStaffManagement.confirmModals.approveWithdraw.confirm')}
        onConfirm={handleApproveWithdrawConfirm}
        onCancel={() => setShowApproveWithdrawConfirm(false)}
      />

      <ConfirmModal
        open={showRejectWithdrawConfirm}
        title={t('medicalStaffManagement.confirmModals.rejectWithdraw.title')}
        message={`${t('medicalStaffManagement.confirmModals.rejectWithdraw.message', {
          name: editing?.name ?? '',
        })} ${t('medicalStaffManagement.confirmModals.rejectWithdraw.emailNote', {
          name: editing?.name ?? '',
        })}`}
        confirmLabel={t('medicalStaffManagement.confirmModals.rejectWithdraw.confirm')}
        cancelLabel={t('medicalStaffManagement.confirmModals.rejectWithdraw.cancel')}
        loading={rejectWithdrawMutation.isPending}
        loadingLabel={t('medicalStaffManagement.confirmModals.rejectWithdraw.confirm')}
        onConfirm={handleRejectWithdrawConfirm}
        onCancel={() => setShowRejectWithdrawConfirm(false)}
      />

      <MedicalStaffResendPreviewModal
        open={resendPreviewOpen}
        name={editing?.name ?? ''}
        email={editing?.email ?? ''}
        departmentLabel={translateDepartment(editing?.department ?? '')}
        jobRoleLabel={translateJobRole(editing?.jobRole ?? '')}
        sentAt={resendSentAt}
        submitting={resendMutation.isPending}
        onEdit={() => setResendPreviewOpen(false)}
        onConfirm={handleResend}
      />
    </>
  );
}
